import {
  Alert,
  Button,
  Empty,
  Spin,
  Table,
} from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import React from 'react'
import { browserHistory } from 'react-router'
import type {
  AuditRun,
  OverlapEmployeeSummary,
} from '../api/enrollment-overlap.types'
import EmployeeActionsMenu from '../components/EmployeeActionsMenu'
import QueryErrorState from '../components/QueryErrorState'
import RunSummary from '../components/RunSummary'
import SnapshotNote from '../components/SnapshotNote'
import { useRunEmployeesQuery } from '../hooks/useEnrollmentOverlapQueries'
import { CLOCK_TICK_MS, useNow } from '../hooks/useNow'
import { usePagingParams } from '../hooks/usePagingParams'
import { enrollmentOverlapEmployeePath, enrollmentOverlapPath } from '../paths'
import { handleRowLinkClick } from '../utils/link-click'
import { PAGE_SIZE_OPTIONS } from '../utils/paging'
import {
  displayEmployeeName,
  formatDate,
  hasNoNames,
  isRunStalled,
  orDash,
} from '../utils/run-display'

interface RunDetailProps {
  params: { runId: string }
}

const FAILED_FALLBACK_MESSAGE = 'The audit failed.'

function RunStateNotice({ run, now }: { run: AuditRun; now: number }) {
  if (run.status === 'FAILED') {
    return (
      <Alert
        type="error"
        showIcon
        message="This audit run failed"
        description={orDash(run.errorMessage ?? FAILED_FALLBACK_MESSAGE)}
      />
    )
  }
  if (run.status === 'RUNNING') {
    const stalled = isRunStalled(run, now)
    return (
      <Alert
        type={stalled ? 'warning' : 'info'}
        showIcon
        icon={stalled ? undefined : <Spin size="small" />}
        message={stalled ? 'Possibly stalled' : 'Run in progress'}
        description={
          stalled
            ? 'This run has been going for more than 20 minutes and is probably abandoned. It will be marked as failed the next time an audit is triggered.'
            : 'This page refreshes automatically until the run finishes.'
        }
      />
    )
  }
  return null
}

export default function RunDetail({ params }: RunDetailProps) {
  const { runId } = params
  const { page, pageSize, setPaging } = usePagingParams()
  const { data, isLoading, error, refetch } = useRunEmployeesQuery(runId, {
    page,
    pageSize,
  })

  const columns: ColumnsType<OverlapEmployeeSummary> = [
    {
      title: 'Employee Public Key',
      key: 'employeePublicKey',
      render: (_, employee) => (
        <span style={{ color: '#8c8c8c' }}>{employee.employeePublicKey}</span>
      ),
    },
    {
      title: 'Name',
      key: 'fullName',
      render: (_, employee) => (
        <a
          href={enrollmentOverlapEmployeePath(
            runId,
            employee.employeePublicKey
          )}
          onClick={(event) =>
            handleRowLinkClick(event, () =>
              browserHistory.push(
                enrollmentOverlapEmployeePath(runId, employee.employeePublicKey)
              )
            )
          }
        >
          {displayEmployeeName(employee)}
        </a>
      ),
    },
    {
      title: 'Overlapping pairs',
      dataIndex: 'overlapCount',
      key: 'overlapCount',
      align: 'right',
    },
    {
      title: 'Enrollments involved',
      dataIndex: 'enrollmentCount',
      key: 'enrollmentCount',
      align: 'right',
    },
    {
      title: 'First overlap starts',
      key: 'firstOverlapStart',
      render: (_, employee) => formatDate(employee.firstOverlapStart),
    },
    {
      title: 'Last overlap ends',
      key: 'lastOverlapEnd',
      render: (_, employee) => formatDate(employee.lastOverlapEnd),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, employee) => (
        <EmployeeActionsMenu
          runId={runId}
          employeePublicKey={employee.employeePublicKey}
        />
      ),
    },
  ]

  const run = data?.run
  // Re-render while RUNNING so "Possibly stalled" appears once 20 minutes pass.
  const now = useNow(run?.status === 'RUNNING' ? CLOCK_TICK_MS : null)
  const isSucceeded = run?.status === 'SUCCEEDED'

  const renderBody = () => {
    if (error && !data) {
      return (
        <QueryErrorState
          error={error}
          onRetry={() => void refetch()}
          notFoundTitle="Run not found"
          notFoundSubTitle="This audit run does not exist or the link is incorrect."
        />
      )
    }
    if (isLoading || !run) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      )
    }
    return (
      <>
        <RunSummary run={run} />
        <RunStateNotice run={run} now={now} />
        {isSucceeded ? (
          <>
            <SnapshotNote startedAt={run.startedAt} />
            {hasNoNames(data?.items ?? []) ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Names are not available for this page"
                description="They may be missing on record or temporarily unavailable. The rest of the report is unaffected."
                action={
                  <Button size="small" onClick={() => void refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : null}
            <Table<OverlapEmployeeSummary>
              dataSource={data?.items ?? []}
              columns={columns}
              rowKey={(employee) => employee.employeePublicKey}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No overlapping enrollments found"
                  />
                ),
              }}
              onRow={(employee) => ({
                onClick: () =>
                  browserHistory.push(
                    enrollmentOverlapEmployeePath(
                      runId,
                      employee.employeePublicKey
                    )
                  ),
                style: { cursor: 'pointer' },
              })}
              pagination={{
                current: page,
                pageSize,
                total: data?.total ?? 0,
                showSizeChanger: true,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                showTotal: (total) => `${total.toLocaleString()} employees`,
                onChange: (nextPage, nextPageSize) =>
                  setPaging(nextPage, nextPageSize),
              }}
            />
          </>
        ) : null}
      </>
    )
  }

  return (
    <Page
      title="Employees with overlapping enrollments"
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Home', path: '/' },
            { title: 'Enrollment Overlap', path: enrollmentOverlapPath() },
            { title: 'Run' },
          ]}
        />
      }
    >
      {renderBody()}
    </Page>
  )
}
